import {
  type FC, useMemo,
} from 'react';
import { useMapSettingsFileAttachmentsMapSelector } from '~/store/mapSettings/fileAttachments/fileAttachments.selectors';
import {
  useAreMarkerLabelsDisabled, useMapSettingsMarkersGeneralSelector, useMapSettingsMarkersTextureAnchors,
} from '~/store/mapSettings/makersGeneral/mapSettingsMarkersGeneral.selectors';
import {
  DEFAULT_MARKER_STYLE, useMarkerSets,
} from '../../store/frontendState/markerSets/markerSets.selectors';
import { type QuadrilateralDimensions } from '../types/coordinateSystem/coordinateSystem';
import {
  type MarkerEntityStyle, type MarkerLabelStyles, MarkerStyleType,
} from '../types/marker.types';
import { MarkerSettingType } from '../types/markers/visualSettings.enums';
import { type MarkerSettings } from '../types/markers/visualSettings.types';
import { useFileAttachmentUrls } from '../utils/files/useFileUrls';
import {
  getFallbackStandardMarkerSettings, getInitialMarker, transformMarkerVisualSettingsForHTMLVisualiser,
} from '../utils/markers/markersVisualSettings.helpers';
import {
  DEFAULT_MARKER_STYLE_ID, INITIAL_LABEL,
} from '../utils/markers/markerVisualSettings.constants';
import { LabelOrMarkerVisualizerComponent } from './labelOrMarkerVisualizer.component';

/**
 * @prop {string} underlayColor - The background color underneath the label.
 *  For labels, this color is blended with the label’s fill based on its opacity, to emulate a see-through fill color.
 *  **Important:** If your label is rendered over a non-standard background,
 *  you must explicitly provide an underlayColor. Otherwise, the default fallback may result in visual inconsistencies.
 */
type LabelOrMarkerVisualizerContainerProps = Readonly<{
  className?: string;
  labelText?: string;
  markerSettings: MarkerSettings;
  maxDimensions: QuadrilateralDimensions;
  maxSize?: number; //everything above this size will be considered 100%
  maxSizeEasing?: boolean;
  underlayColor?: string;
  visualSettingsType?: MarkerSettingType;
}>;

export const LabelOrMarkerVisualizerContainer: FC<LabelOrMarkerVisualizerContainerProps> = props => {
  const markerSets = useMarkerSets();
  const markersGeneralSettings = useMapSettingsMarkersGeneralSelector();
  const fileAttachmentMap = useMapSettingsFileAttachmentsMapSelector();
  const { fileAttachmentIdsToUrls } = useFileAttachmentUrls();
  const { getMarkerTextureAnchor } = useMapSettingsMarkersTextureAnchors();
  const markerLabelsDisabled = useAreMarkerLabelsDisabled();

  const { markerSettings, visualSettingsType } = props;

  const isLabelConditionMet = !markerSettings.useMarker
    && (
      (visualSettingsType === MarkerSettingType.Label) ||
      ((visualSettingsType !== MarkerSettingType.Marker) && !markerLabelsDisabled)
    );

  const alteredSettings = transformMarkerVisualSettingsForHTMLVisualiser(markerSettings);

  const visualSettings: MarkerEntityStyle = useMemo(() => {
    if (isLabelConditionMet) {
      const label = alteredSettings.label ?? INITIAL_LABEL;
      const labelStyle: MarkerLabelStyles = {
        ...label,
        type: 'label',
        offsetProps: { type: 'default' },
      };
      return labelStyle;
    }

    const marker = alteredSettings.marker ??
      getInitialMarker(markersGeneralSettings.defaultMarkerSetId || DEFAULT_MARKER_STYLE_ID);

    if (marker.styleType === MarkerStyleType.STANDARD) {
      return {
        ...(markerSets[marker.styleId] ?? DEFAULT_MARKER_STYLE),
        opacity: marker.opacity,
        selectedColor: marker.selectedColor,
        size: marker.size,
      };
    }
    else {
      const attachmentId = marker.fileAttachmentId;
      const attachment = fileAttachmentMap.get(attachmentId);
      const fileUrl = fileAttachmentIdsToUrls.get(attachmentId);
      if (!attachment || !fileUrl) {
        const customToStandardFallbackSettings = getFallbackStandardMarkerSettings(markersGeneralSettings);
        return {
          ...(markerSets[customToStandardFallbackSettings.styleId] ?? DEFAULT_MARKER_STYLE),
          opacity: customToStandardFallbackSettings.opacity,
          selectedColor: customToStandardFallbackSettings.selectedColor,
          size: customToStandardFallbackSettings.size,
        };
      }

      return {
        marker: {
          fileId: attachment.fileId,
          anchor: getMarkerTextureAnchor(attachment.id),
          dimensions: fileUrl.original.resolution,
          path: fileUrl.original.url,
        },
        type: 'marker',
        markerType: MarkerStyleType.CUSTOM,
        size: marker.size,
      };
    }
  }, [isLabelConditionMet, alteredSettings.marker, alteredSettings.label, markersGeneralSettings, markerSets,
    fileAttachmentMap, fileAttachmentIdsToUrls, getMarkerTextureAnchor]);

  return (visualSettings ? (
    <LabelOrMarkerVisualizerComponent
      className={props.className}
      dimensions={props.maxDimensions}
      labelText={props.labelText}
      maxSize={props.maxSize}
      maxSizeEasing={props.maxSizeEasing}
      selectedMarkerStyle={visualSettings}
      underlayColor={props.underlayColor}
    />
  ) : null);
};
